import React, { createContext, useState, useRef, useEffect, useContext } from "react";
import * as signalR from "@microsoft/signalr";
import { baseUrl } from "core/UrlEndpoints";
import { useAuth0 } from "react-auth0-spa";
import { UserContext } from "./UserContext";
import AgentService from "core/api/AgentService";

const SignalREvents = {
    DISPATCH_911_REQUEST: "Dispatch911Request",
    DISPATCH_911_CLAIMED: "Dispatch911Claimed"
};

const isAgentActive = (status) => status === "Online" || status === "Busy";

const playAlertBeep = () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    let chimeCount = 0;

    const chime = () => {
        if (chimeCount >= 3) return;

        const playTone = (freq, startTime, duration) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            osc.type = 'sine';

            gain.gain.setValueAtTime(0, ctx.currentTime + startTime);
            gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + startTime + 0.05);
            gain.gain.linearRampToValueAtTime(0, ctx.currentTime + startTime + duration);

            osc.start(ctx.currentTime + startTime);
            osc.stop(ctx.currentTime + startTime + duration);
        };

        playTone(523, 0, 0.3);
        playTone(659, 0.2, 0.4);

        chimeCount++;
        setTimeout(chime, 1400);
    };

    chime();
};

export const SignalRNotificationContext = createContext({ dispatchRequests: [], removeDispatchRequest: () => { }, connectionStatus: "disconnected", dispatchRegistrationStatus: "not-registered" });

export const SignalRNotificationProvider = ({ children }) => {
    const [dispatchRequests, setDispatchRequests] = useState([]);
    const [connectionStatus, setConnectionStatus] = useState("disconnected");
    const [dispatchRegistrationStatus, setDispatchRegistrationStatus] = useState("not-registered");
    const connectionRef = useRef(null);
    
    const { getTokenSilently, user } = useAuth0();
    const { userData, userStatusRef, userHasAnyPermissions, prevUserStatus } = useContext(UserContext);

    const addDispatchRequest = (request) => {
        setDispatchRequests(prev => {
            if (prev.some(r => r.callId === request.callId)) return prev;
            return [...prev, request];
        });
    };

    const removeDispatchRequest = (callId) => {
        setDispatchRequests(prev => prev.filter(r => r.callId !== callId));
    };

    // Load pending dispatches when returning from Offline or Break.
    useEffect(() => {
        if (!userHasAnyPermissions(["all:agent", "admin"]) || !user?.email) return;

        const isActive = isAgentActive(userData.status);

        // Clear dispatches when going offline or on break.
        if (!isActive) {
            setDispatchRequests([]);
            return;
        }

        // Only load when transitioning FROM Offline to active
        // Online to Busy does not need to reload pending dispatches.
        if ((prevUserStatus === "Offline" || prevUserStatus === "Break") && isActive) {
            const loadPendingDispatches = async () => {
                try {
                    const pending = await AgentService.getPendingDispatches(user.email);
                    if (pending && Array.isArray(pending)) {
                        pending.forEach(addDispatchRequest);
                    }
                } catch (err) {
                    console.error("Failed to load pending dispatches:", err);
                }
            };

            loadPendingDispatches();
        }
    }, [user?.email, userData.status]);

    // Connect to SignalR hub
    useEffect(() => {
        if (!userHasAnyPermissions(["all:agent", "admin"])) return;

        let isMounted = true;

        const connectToHub = async () => {
            try {
                const token = await getTokenSilently();

                const connection = new signalR.HubConnectionBuilder()
                    .withUrl(`${baseUrl}/hubs/agent`, {
                        accessTokenFactory: () => token
                    })
                    .withAutomaticReconnect()
                    .configureLogging(signalR.LogLevel.Warning)
                    .build();

                // Connection lifecycle handlers
                connection.onreconnecting(() => {
                    if (isMounted) {
                        setConnectionStatus("reconnecting");
                        setDispatchRegistrationStatus("not-registered");
                    }
                });

                connection.onreconnected(async () => {
                    if (!isMounted) return;
                    setDispatchRegistrationStatus("not-registered");
                    setConnectionStatus("connected");
                });

                connection.onclose(() => {
                    if (isMounted) {
                        setConnectionStatus("disconnected");
                        setDispatchRegistrationStatus("not-registered");
                    }
                });


                // Notification handlers
                connection.on(SignalREvents.DISPATCH_911_REQUEST, (notification) => {
                    if (isMounted) {
                        addDispatchRequest(notification);
                        playAlertBeep();
                    }
                });

                connection.on(SignalREvents.DISPATCH_911_CLAIMED, (callId) => {
                    if (isMounted) removeDispatchRequest(callId);
                });

                await connection.start();
                
                if (!isMounted) {
                    connection.stop();
                    return;
                }

                connectionRef.current = connection;
                setDispatchRegistrationStatus("not-registered");
                setConnectionStatus("connected");


            } catch (err) {
                console.error("SignalR connection failed:", err);
                if (isMounted) {
                    setConnectionStatus("error");
                    setDispatchRegistrationStatus("not-registered");
                }
            }
        };

        connectToHub();

        return () => {
            isMounted = false;
            if (connectionRef.current) {
                connectionRef.current.stop();
                connectionRef.current = null;
            }
        };
    }, []);

    
    useEffect(() => {
        const connection = connectionRef.current;
        let isCurrent = true;

        if (connectionStatus !== "connected" || !connection || connection.state !== signalR.HubConnectionState.Connected) {
            setDispatchRegistrationStatus("not-registered");
            return () => { isCurrent = false; };
        }

        const shouldJoin = isAgentActive(userData.status);
        setDispatchRegistrationStatus(shouldJoin ? "registering" : "not-registered");

        let registrationRequest;
        if (shouldJoin) {
            registrationRequest = connection.invoke("JoinOnlineAgents");
        } else {
            registrationRequest = connection.invoke("LeaveOnlineAgents");
        }

        registrationRequest.then(() => {
            if (!isCurrent || connection.state !== signalR.HubConnectionState.Connected) return;
            setDispatchRegistrationStatus(shouldJoin ? "registered" : "not-registered");
        }).catch(err => {
            console.error("Failed to update dispatch group:", err);
            if (isCurrent) {
                setDispatchRegistrationStatus("error");
            }
        });

        return () => { isCurrent = false; };
    }, [userData.status, connectionStatus]);

    return (
        <SignalRNotificationContext.Provider value={{ dispatchRequests, removeDispatchRequest, connectionStatus, dispatchRegistrationStatus }}>
            {children}
        </SignalRNotificationContext.Provider>
    );
};
